import { ThemeCssHooks, ThemeDeployment, ThemeDocumentSchema1, ThemeIssue, ThemeRequirements } from './types';

export const listCapabilities = ['surfaces', 'editableThemePaths', 'layouts', 'fonts'] as const;

export const flagCapabilities = ['customCss'] as const;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const strings = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length <= 128 &&
  value.every((entry) => typeof entry === 'string' && entry.length <= 128);

const versions = (value: unknown): value is number[] =>
  Array.isArray(value) && value.length <= 128 && value.every((entry) => Number.isSafeInteger(entry) && entry > 0);

export function isThemeDeployment(value: unknown): value is ThemeDeployment & Record<string, unknown> {
  if (!isRecord(value) || !isRecord(value.capabilities)) return false;

  const capabilities = value.capabilities;

  return (
    typeof value.contractHash === 'string' &&
    /^sha256-[a-f0-9]{64}$/.test(value.contractHash) &&
    versions(value.supportedSchemaVersions) &&
    versions(value.supportedPreviewProtocolVersions) &&
    listCapabilities.every((key) => strings(capabilities[key])) &&
    flagCapabilities.every((key) => typeof capabilities[key] === 'boolean')
  );
}

export function isThemeRequirements(value: unknown): value is ThemeRequirements {
  if (!isRecord(value)) return false;
  return Object.entries(value).every(([key, entry]) => {
    if (key === 'themeSchemaVersion' || key === 'previewProtocolVersion')
      return Number.isSafeInteger(entry) && Number(entry) > 0;
    if (listCapabilities.some((name) => name === key)) return strings(entry);
    return flagCapabilities.some((name) => name === key) && typeof entry === 'boolean';
  });
}

// Check the fields the adapter reads; this is not a second theme schema validator.
export function hasThemeDocumentShape(value: unknown): value is ThemeDocumentSchema1 {
  return (
    isRecord(value) &&
    value.themeSchemaVersion === 1 &&
    ['header', 'typography', 'background', 'button', 'question', 'progress', 'widget'].every((key) =>
      isRecord(value[key]),
    ) &&
    isRecord(value.background) &&
    typeof value.background.color === 'string' &&
    typeof value.customCss === 'string'
  );
}
const position = (value: unknown) => value === undefined || (Number.isSafeInteger(value) && Number(value) > 0);

export function isThemeIssues(value: unknown): value is ThemeIssue[] {
  return (
    Array.isArray(value) &&
    value.length <= 128 &&
    value.every(
      (issue) =>
        isRecord(issue) &&
        typeof issue.code === 'string' &&
        typeof issue.path === 'string' &&
        position(issue.line) &&
        position(issue.column),
    )
  );
}

export function isThemeCssHooks(value: unknown): value is ThemeCssHooks {
  return isRecord(value) && strings(value.parts) && strings(value.questionTypes) && strings(value.states);
}
